import {
  AktionsErgebnisSchema,
  MeResponseSchema,
  MeineModuleResponseSchema,
  ModulTermineResponseSchema,
} from '@ksb/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiAufruf, apiSenden } from './client';

export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: () => apiAufruf('me', MeResponseSchema),
    staleTime: 5 * 60_000,
  });
}

export function useMeineModule() {
  return useQuery({
    queryKey: ['meine-module'],
    queryFn: () => apiAufruf('meine-module', MeineModuleResponseSchema),
  });
}

export function useModulTermine(modulId: number) {
  return useQuery({
    queryKey: ['modul-termine', modulId],
    queryFn: () => apiAufruf(`module/${modulId}/termine`, ModulTermineResponseSchema),
    enabled: Number.isInteger(modulId) && modulId > 0,
  });
}

/**
 * Nach jeder Aktion (auch einer gescheiterten, z. B. 409 „ausgebucht“) alle Teilnehmerdaten neu laden. Die Mutation
 * gilt erst als abgeschlossen, wenn die Daten neu da sind, damit nie ein veralteter Stand zu sehen ist.
 */
function useNeuLaden() {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      // „all“: Die Startseite ist während der Aktion nicht offen, soll aber beim Zurückspringen sofort stimmen.
      queryClient.invalidateQueries({ queryKey: ['meine-module'], refetchType: 'all' }),
      queryClient.invalidateQueries({ queryKey: ['modul-termine'] }),
    ]);
}

export function useBuchen() {
  const neuLaden = useNeuLaden();
  return useMutation({
    mutationFn: (terminId: number) => apiSenden('buchungen', AktionsErgebnisSchema, { terminId }),
    onSettled: neuLaden,
  });
}

export function useUmbuchen() {
  const neuLaden = useNeuLaden();
  return useMutation({
    mutationFn: (p: { buchungId: number; terminId: number }) =>
      apiSenden(`buchungen/${p.buchungId}/umbuchen`, AktionsErgebnisSchema, {
        terminId: p.terminId,
      }),
    onSettled: neuLaden,
  });
}

export function useStornieren() {
  const neuLaden = useNeuLaden();
  return useMutation({
    mutationFn: (buchungId: number) =>
      apiSenden(`buchungen/${buchungId}/stornieren`, AktionsErgebnisSchema, {}),
    onSettled: neuLaden,
  });
}
